import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as faIcons from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import MaxPageResultsSelect from 'modules/Common/table/MaxPageResultsSelect';
import TableActionButton from './TableActionButton';
import Pagination from './Pagination';
import CgButton from 'modules/Common/ui/CgButton';
import CgPanel from 'modules/Common/layout/CgPanel';
import CgNoResults from 'modules/Common/ui/CgNoResults';
import CgEmptyState from 'modules/Common/ui/CgEmptyState';
import { CgToggle } from 'modules/Common/toggle/CgToggle';
import CgUpdatePopover from 'modules/Common/freemium/CgUpdatePopover';
import BulkActionDock from 'modules/Common/ui/BulkActionDock';
import CgConfirmDialog from 'modules/Common/ui/CgConfirmDialog';
import CgButtonDropdown from 'modules/Common/ui/CgButtonDropdown';
import CgCheckbox from 'modules/Common/form/CgCheckbox';
import CgColumnFilter from 'modules/Common/table/CgColumnFilter';
import CgPopover from 'modules/Common/ui/CgPopover';
import CgCopyButton from 'modules/Common/ui/CgCopyButton';
import CgTableSkeleton from 'modules/Common/table/CgTableSkeleton';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatNumber as formatNumberByConvention } from 'helpers/numberFormat';
import { useTranslation } from 'react-i18next';

export function sanitizeColumnOrderPreference(preferredOrder, validKeysInDefaultOrder) {
  const valid = new Set(validKeysInDefaultOrder);
  const seen = new Set();
  const result = [];
  for (const key of preferredOrder || []) {
    if (valid.has(key) && !seen.has(key)) {
      seen.add(key);
      result.push(key);
    }
  }
  for (const key of validKeysInDefaultOrder) {
    if (!seen.has(key)) result.push(key);
  }
  return result;
}

export function applyCgTableHeaderPreferences(allHeaders, lockedKeys, columnOrder, hiddenColumns) {
  const hidden = new Set(hiddenColumns || []);
  const locked = new Set(lockedKeys || []);
  const defaultUnlockedOrder = allHeaders.filter((h) => !locked.has(h.key)).map((h) => h.key);
  const mergedOrder = sanitizeColumnOrderPreference(columnOrder, defaultUnlockedOrder);
  const byKey = Object.fromEntries(allHeaders.map((h) => [h.key, h]));
  const lockedHeaders = allHeaders.filter((h) => locked.has(h.key));
  const orderedUnlocked = mergedOrder.map((k) => byKey[k]).filter(Boolean);
  return [...lockedHeaders, ...orderedUnlocked].filter((h) => !hidden.has(h.key));
}

// Shared Tailwind classes for table column headers — matches the Form v2 labelClass.
const thClass =
  'text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50 select-none';

function TableHeaderCellContent({
  header,
  columnFilters,
  columnFilterValues,
  handleColumnFilterChange,
}) {
  // Every filter targeting this column shares ONE icon: multiple matches
  // render a grouped popover (e.g. Supplier ID + Supplier name on the
  // composite Name column) instead of a row of funnel icons.
  const matchingFilters = columnFilters.filter((filter) => {
    if (Array.isArray(filter.columnKey)) {
      return filter.columnKey.includes(header.key);
    }
    return filter.columnKey === header.key;
  });

  const anyActive = matchingFilters.some((filter) => {
    const value = columnFilterValues[filter.name];
    return value && (Array.isArray(value) ? value.length > 0 : true);
  });

  return (
    <div className="flex min-w-0 flex-1 items-center justify-between gap-1">
      <span className="min-w-0">{header.label}</span>
      <div className="flex shrink-0 items-center gap-1">
        {matchingFilters.length > 0 && (
          <CgColumnFilter
            key={`${header.key}-filter`}
            filters={matchingFilters}
            values={columnFilterValues}
            onChange={handleColumnFilterChange}
            isActive={anyActive}
          />
        )}
      </div>
    </div>
  );
}

function SortableTableHeaderCell({
  header,
  columnFilters,
  columnFilterValues,
  handleColumnFilterChange,
}) {
  const { t } = useTranslation('common');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: header.key,
  });

  const dndStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : undefined,
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.12)' : undefined,
  };

  const thHoverGrip =
    '[&:hover_.cg-table-reorder-grip]:pointer-events-auto [&:hover_.cg-table-reorder-grip]:opacity-100 [&:hover_.cg-table-reorder-grip]:bg-white [&:hover_.cg-table-reorder-grip]:text-gray-600 [&:hover_.cg-table-reorder-grip]:shadow-sm [&:hover_.cg-table-reorder-grip]:ring-1 [&:hover_.cg-table-reorder-grip]:ring-gray-200/90';

  const isIcon = header.type === 'icon';

  return (
    <th
      ref={setNodeRef}
      style={{
        ...dndStyle,
        ...(isIcon ? { width: '48px' } : {}),
        ...(header.width && !isIcon ? { width: header.width } : {}),
      }}
      className={`relative overflow-visible ${isIcon ? 'pl-3 pr-1' : 'px-3'} py-2 ${thClass} ${thHoverGrip}`}
    >
      <TableHeaderCellContent
        header={header}
        columnFilters={columnFilters}
        columnFilterValues={columnFilterValues}
        handleColumnFilterChange={handleColumnFilterChange}
      />
      <button
        type="button"
        className={`cg-table-reorder-grip absolute left-1 top-1/2 z-20 flex h-7 w-7 -translate-y-1/2 cursor-grab touch-none items-center justify-center rounded text-gray-400 transition-all hover:text-primaryColor/80 focus:outline-none focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-primaryColor/40 active:cursor-grabbing ${
          isDragging ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
        aria-label={t('common:tableControls.reorderColumnAria', {
          column: header.label,
          defaultValue: 'Reorder column {{column}}',
        })}
        title={t('table.dragToReorder', 'Drag to reorder')}
        {...attributes}
        {...listeners}
      >
        <FontAwesomeIcon
          icon={faIcons.faGripVertical}
          size="sm"
        />
      </button>
    </th>
  );
}

const Table = ({
  headers,
  data,
  actions,
  values,
  title,
  filter,
  tableButtons,
  pagination,
  actionButtons = [],
  selectable = false,
  // Per-row selection gate: `(row) => boolean`. Rows it rejects get a disabled
  // checkbox and are skipped by "select all", so a table holding two kinds of
  // row can offer bulk verbs that only ONE kind supports (operator-parity
  // phase 19: a monitoring-plan row has no delete). Absent ⇒ every row is
  // selectable, which is what every existing caller gets.
  isRowSelectable = null,
  bulkActions = [],
  onSelect = () => {},
  selectedItems = [],
  selectionSummary = '',
  onPrimaryClick = () => {},
  loading = false,
  checkboxLoading = false,
  showActions = true,
  // When true (default) a live selection surfaces the table's bulk verbs
  // (actions with bulkAction !== false) in an automatic floating
  // BulkActionDock. Pages that wire their own selectionDock opt out with
  // showSelectionToolbar={false} so selection isn't doubled up.
  showSelectionToolbar = true,
  // Bulk-selection action row (usually <BulkActionDock/>) rendered on its
  // own strip between the toolbar and the card, so selection verbs stay
  // with the rest of the table controls instead of below the table where
  // they scroll off-screen. Pass null when nothing is selected.
  selectionDock = null,
  // Opt-in hover/focus reveal for the trailing actions cell. Tables that
  // previously relied on always-visible row actions keep that behaviour by
  // default; the select-to-reveal pages (suppliers/installations) set this so
  // their re-enabled action cell only shows on row hover or keyboard focus.
  revealActionsOnHover = false,
  // Opt-in `table-layout: fixed`. Off by default: the auto layout that every
  // other table relies on sizes columns to their content, which is right for
  // grids where the data varies. Switch it on for tables whose columns must
  // not move between pages — a trailing CTA column that shifts as names get
  // longer is the usual reason. Widths come from each header's `width`
  // (any CSS length); headers without one share what's left equally, which is
  // how `table-layout: fixed` distributes unsized columns.
  fixedLayout = false,
  tableKey = null,
  animate = true,
  emptyStateVariant = 'default',
  onEmptyStateImport,
  onEmptyStateCreate,
  onEmptyStateGenerateReport,
  emptyStateCreateLabel,
  emptyStateImportLabel,
  emptyStateTitle,
  emptyStateDescription,
  onEmptyStateNavigate,
  emptyStateNavigateLabel,
  // When the caller's own search/segments are active, the table renders the
  // `no-results` intent ("No matches for the current filters" + Clear
  // filters) instead of the first-run empty state — so search copy never
  // shows on a page with no active search. Column-header filters are detected
  // automatically; pass `isFiltered` for segment / search-form state the
  // table can't see, and `onClearFilters` to reset it.
  isFiltered = false,
  onClearFilters,
  onColumnOrderChange = null,
  columnReorderLockedKeys = [],
  flat = false,
  // With `bare`, the table draws no outer borders or rounding of its own —
  // for embedding inside a host card that owns the edges. Implies flat
  // toolbar treatment; rows and toolbar run the full card width.
  bare = false,
  getRowClassName = null,
  // Custom node rendered in the toolbar's LEFT section before active filter
  // chips. Use this to slot in workflow-segments (e.g. SmartSegments) so
  // they sit on the same row as the Filter button instead of stacking
  // above the table as an orphan strip.
  toolbarLeftPrefix = null,
  paginationLabel = null,
}) => {
  const { t } = useTranslation('common');
  const [selectAll, setSelectAll] = useState(false);
  // Pending confirmation for a destructive bulk verb from the automatic
  // dock — holds { action, primaryBtn } until confirmed or dismissed.
  const [bulkConfirm, setBulkConfirm] = useState(null);
  // Same, for a per-row overlay verb — holds { action, primaryBtn, item }.
  // Row and bulk each own their own slot so a row confirm can never inherit
  // the dock's selection payload.
  const [rowConfirm, setRowConfirm] = useState(null);
  // R4 (S7 fix batch 3): a confirm's `onClick` may return a Promise (a
  // mutation) for a non-idempotent verb (e.g. withdraw). While it's in
  // flight the dialog stays open and locked (CgConfirmDialog `loading`)
  // instead of closing immediately and leaving the trigger clickable for a
  // duplicate submit. Synchronous/void onClick (the vast majority of
  // callers) behaves exactly as before: never pending, closes on the same
  // tick.
  const [rowConfirmPending, setRowConfirmPending] = useState(false);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnFilterValues, setColumnFilterValues] = useState({});
  const [columnFilterChangeHandler, setColumnFilterChangeHandler] = useState(null);

  useEffect(() => {
    if (selectedItems.length > 0) {
      const validSelectedItems = selectedItems.filter((selected) =>
        values?.some((item) => item._id === selected._id),
      );
      if (validSelectedItems.length !== selectedItems.length) {
        onSelect(validSelectedItems);
      }
    }
  }, [values, selectedItems]);

  const canSelectRow = (row) =>
    typeof isRowSelectable === 'function' ? !!isRowSelectable(row) : true;

  const handleRowSelect = (item, checked) => {
    if (!onSelect || !canSelectRow(item)) return;
    let newSelectedItems;
    if (checked) {
      newSelectedItems = [...selectedItems, item];
    } else {
      newSelectedItems = selectedItems.filter((selected) => selected._id !== item._id);
    }
    onSelect(newSelectedItems);
  };

  const handleSelectAll = (checked) => {
    if (!onSelect) return;
    setSelectAll(checked);
    onSelect(checked ? (values || []).filter(canSelectRow) : []);
  };

  useEffect(() => {
    // Measured against the SELECTABLE rows: on a page holding one gated-out
    // row the header checkbox could otherwise never read as fully checked.
    const selectableCount = (values || []).filter(canSelectRow).length;
    const allSelected = selectableCount > 0 && selectedItems.length === selectableCount;
    setSelectAll(allSelected);
  }, [selectedItems, values, isRowSelectable]);

  // The 4th argument (preset payload) is intentionally ignored here —
  // saved-view UI lives in the segment pills row via usePresetSubscription,
  // which listens to the same cgTableFilterReady event.
  const handleFiltersReady = useCallback((filters, formData, onFormChange) => {
    const validColumnFilters = filters.filter((f) => f.columnKey);
    setColumnFilters(validColumnFilters);
    setColumnFilterValues(formData);
    setColumnFilterChangeHandler(() => onFormChange);
  }, []);

  useEffect(() => {
    const tableId = `cgTable_${Math.random().toString(36).substr(2, 9)}`;

    const handleGlobalFilterReady = (event) => {
      if (event.detail.tableId && event.detail.tableId !== tableId) {
        return;
      }
      const { filters, formData, onFormChange, presetData } = event.detail;
      if (filters && formData && onFormChange) {
        handleFiltersReady(filters, formData, onFormChange, presetData);
      }
    };

    window.addEventListener('cgTableFilterReady', handleGlobalFilterReady);
    window._cgTableCurrentId = tableId;

    return () => {
      window.removeEventListener('cgTableFilterReady', handleGlobalFilterReady);
      if (window._cgTableCurrentId === tableId) {
        delete window._cgTableCurrentId;
      }
    };
  }, [handleFiltersReady]);

  const enhancedFilter = useMemo(() => {
    if (!filter || !React.isValidElement(filter)) return filter;
    return React.cloneElement(filter, {
      ...filter.props,
      onFiltersReady: handleFiltersReady,
      tableTitle: title,
      tableKey: tableKey,
      tableContext: typeof window !== 'undefined' ? window.location.pathname : '',
    });
  }, [filter, handleFiltersReady, title, tableKey]);

  const handleColumnFilterChange = useCallback(
    (filterName, value) => {
      const updatedValues = {
        ...columnFilterValues,
        [filterName]: value,
        page: 1,
      };
      if (columnFilterChangeHandler) {
        columnFilterChangeHandler(updatedValues);
      }
    },
    [columnFilterValues, columnFilterChangeHandler],
  );

  const handleClearAllColumnFilters = useCallback(() => {
    if (!columnFilterChangeHandler) return;
    const resetValues = { ...columnFilterValues };
    columnFilters.forEach((f) => {
      resetValues[f.name] = f.type === 'multi-select' ? [] : '';
    });
    columnFilterChangeHandler(resetValues);
  }, [columnFilters, columnFilterValues, columnFilterChangeHandler]);

  const activeColumnFilters = useMemo(() => {
    return columnFilters
      .filter((filter) => {
        const value = columnFilterValues[filter.name];
        return (
          value !== null &&
          value !== undefined &&
          value !== '' &&
          (Array.isArray(value) ? value.length > 0 : true)
        );
      })
      .map((filter) => {
        const value = columnFilterValues[filter.name];
        let displayValue = value;

        if (filter.type === 'select') {
          const option = filter.options?.find((opt) => opt.value === value);
          displayValue = option ? option.label : value;
        } else if (filter.type === 'multi-select' && Array.isArray(value)) {
          if (value.length === 1) {
            const option = filter.options?.find((opt) => opt.value === value[0]);
            displayValue = option ? option.label : value[0];
          } else {
            displayValue = t('common:filter.selectedCount', { count: value.length });
          }
        }

        return {
          key: filter.name,
          label: filter.label,
          value: displayValue,
          columnKey: filter.columnKey,
          affectedColumns: Array.isArray(filter.columnKey) ? filter.columnKey : [filter.columnKey],
        };
      });
  }, [columnFilters, columnFilterValues, t]);

  const formatNumber = (number, column) => {
    if (number === null || number === undefined || isNaN(number)) {
      return '';
    }
    const options = column.numberFormat || {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4,
    };
    return formatNumberByConvention(number, options);
  };

  const isIdColumn = (column) => {
    const key = column.key.toLowerCase();
    const label = String(column.label || '').toLowerCase();
    return (
      key.includes('id') || label.includes('id') || key.includes('name') || label.includes('name')
    );
  };

  /**
   * Whether a column's cells offer a copy button.
   *
   * A column may SAY so — `copyable: true` / `copyable: false` — and where it
   * does not, the heuristic above stands in as the default. The heuristic came
   * first and is kept exactly as it was, because every table in the app is
   * relying on it: any column whose key or label mentions "id" or "name" gets a
   * copy button nobody asked for by name.
   *
   * That default is fine as a default and wrong as the only answer. It reads a
   * HEADER STRING, so two tables showing the same identifier disagree on the
   * affordance purely over what the column is called — the costs table's EU
   * regime heads its identity column "Import ID" and gets a copy button, its UK
   * regime heads the same column "Import" and gets none. A column that means to
   * be copyable now says so, and one that means not to already could
   * (`noCopyButton`).
   */
  const isCopyable = (column) =>
    typeof column.copyable === 'boolean' ? column.copyable : isIdColumn(column);

  const renderCell = (item, column) => {
    const getUnit = (item, column) => {
      const knownEmptyValues = ['N/A', ''];
      // A function when the unit belongs to the row rather than the column —
      // a quantity column whose rows are tonnes, kilograms and MWh cannot
      // name one unit in its header and be telling the truth.
      let unit = (typeof column.unit === 'function' ? column.unit(item) : column.unit) || '';
      if (column.emissions) {
        unit = 'tCO2e';
      }
      if (unit) {
        unit = ' ' + unit;
      }
      if (!item[column.key] || knownEmptyValues.includes(item[column.key])) {
        return '';
      }
      return unit;
    };

    const copyValue = column.copyKey ? item[column.copyKey] : item[column.key];
    const showCopyButton = !column.noCopyButton && isCopyable(column) && copyValue;

    if (column.render) {
      const renderedContent = column.render(item);
      return (
        <div className="inline-flex items-center whitespace-nowrap text-sm font-semibold text-slate-500">
          {renderedContent}
          {showCopyButton && <CgCopyButton textToCopy={String(copyValue)} />}
        </div>
      );
    }
    if (column.type === 'date') {
      return (
        <div className="text-sm font-semibold text-slate-500">
          {dayjs(item[column.key]).format(column.format || 'DD/MM/YYYY')}
        </div>
      );
    }
    if (column.type === 'number') {
      return (
        <div className="whitespace-nowrap text-sm font-semibold text-slate-500">
          {formatNumber(item[column.key], column)}
          {getUnit(item, column)}
        </div>
      );
    }
    if (column.type === 'icon') {
      // Icon columns are fixed 40px wide — center the glyph instead of
      // letting it fall through to the left-aligned default, which left
      // it hugging the cell edge with dead space on the right.
      return <div className="flex items-center justify-center">{item[column.key] ?? ''}</div>;
    }
    if (column.type === 'boolean') {
      return (
        <div className="text-sm font-semibold text-slate-500">
          {item[column.key] ? (
            <FontAwesomeIcon
              icon={faIcons.faCheckCircle}
              className="text-green-500"
            />
          ) : (
            <FontAwesomeIcon
              icon={faIcons.faXmarkCircle}
              className="text-red-500"
            />
          )}
        </div>
      );
    }
    return (
      <div className="inline-flex items-center whitespace-nowrap text-sm font-semibold text-slate-500">
        {item[column.key] ?? ''}
        {showCopyButton && <CgCopyButton textToCopy={String(copyValue)} />}
      </div>
    );
  };

  const getTableButton = (btn, idx) => {
    if (btn?.type === 'custom') {
      const content = typeof btn.render === 'function' ? btn.render() : btn.component;
      if (!content) return null;
      return (
        <div
          key={btn.key || `table-custom-${idx}`}
          className="flex items-center"
        >
          {content}
        </div>
      );
    }

    if (btn?.type === 'toggle') {
      return (
        <CgToggle
          key={`table-toggle-${idx}`}
          offLabel={btn.offLabel}
          onLabel={btn.onLabel}
          toggled={btn.toggled}
          onClick={btn.onClick}
        />
      );
    }

    if (btn?.isRateLimited) {
      return (
        <CgUpdatePopover
          key={`table-btn-${idx}`}
          hasExceededLimit={btn?.isRateLimited}
          position="bottom"
        >
          <CgButton
            variant={btn.variant}
            label={btn.label}
            size="sm"
            icon={btn.icon}
            className={btn.className}
            id={btn.id}
            data-testid={btn['data-testid']}
            loading={btn.loading}
          />
        </CgUpdatePopover>
      );
    }

    if (btn.dropdown) {
      return (
        <CgButtonDropdown
          key={`table-btn-${idx}`}
          label={btn.label}
          variant={btn.variant}
          icon={btn.icon}
          outlined={false}
          size="sm"
          options={btn.dropdownOptions}
        />
      );
    }

    return (
      <CgButton
        key={`table-btn-${idx}`}
        variant={btn.variant}
        label={btn.label}
        size="sm"
        icon={btn.icon}
        className={btn.className}
        onClick={btn.onClick}
        disabled={btn.disabled}
        id={btn.id}
        loading={btn.loading}
      />
    );
  };

  const columnReorderLockedSet = useMemo(
    () => new Set(columnReorderLockedKeys || []),
    [columnReorderLockedKeys],
  );

  const { lockedTableHeaders, reorderableTableHeaders } = useMemo(() => {
    const locked = [];
    const reorderable = [];
    (headers || []).forEach((h) => {
      if (columnReorderLockedSet.has(h.key)) locked.push(h);
      else reorderable.push(h);
    });
    return { lockedTableHeaders: locked, reorderableTableHeaders: reorderable };
  }, [headers, columnReorderLockedSet]);

  const reorderableColumnIds = useMemo(
    () => reorderableTableHeaders.map((h) => h.key),
    [reorderableTableHeaders],
  );

  const columnDragSensors = useMemo(
    () => [
      { sensor: PointerSensor, options: { activationConstraint: { distance: 6 } } },
      { sensor: KeyboardSensor, options: { coordinateGetter: sortableKeyboardCoordinates } },
    ],
    [],
  );

  const handleColumnDragEnd = useCallback(
    (event) => {
      if (!onColumnOrderChange) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const keys = reorderableColumnIds;
      const oldIndex = keys.indexOf(active.id);
      const newIndex = keys.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return;
      onColumnOrderChange(arrayMove(keys, oldIndex, newIndex));
    },
    [onColumnOrderChange, reorderableColumnIds],
  );

  const headerCellCommonProps = useMemo(
    () => ({
      columnFilters,
      columnFilterValues,
      handleColumnFilterChange,
    }),
    [columnFilters, columnFilterValues, handleColumnFilterChange],
  );

  const useHeaderReorder = Boolean(onColumnOrderChange) && reorderableColumnIds.length > 0;

  // Active filter chips — assembled here as a variable so they render
  // INLINE with the Filter button on the toolbar row instead of occupying
  // their own band below it. Saving the current view lives in the segment
  // pills row (PresetSegmentsTail), not here.
  const inlineFilterControls = (() => {
    if (loading && !data?.length) return null;
    const hasChips = activeColumnFilters.length > 0;
    if (!hasChips) return null;
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        {hasChips && (
          <>
            {activeColumnFilters.map((filter) => (
              <CgPopover
                key={filter.key}
                triggerElement={
                  <div className="bg-primaryColor/5 text-primaryColor-dark border border-primaryColor/20 rounded-md px-2 py-0.5 flex items-center gap-1.5 text-xs max-w-[220px] cursor-default">
                    <div className="flex items-center gap-1 min-w-0">
                      <span className="font-semibold whitespace-nowrap">{filter.label}:</span>
                      <span className="truncate">
                        {Array.isArray(filter.value)
                          ? filter.value.join(', ')
                          : typeof filter.value === 'string' && filter.value.length > 15
                            ? `${filter.value.substring(0, 15)}…`
                            : filter.value}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const clearValue =
                          columnFilters.find((f) => f.name === filter.key)?.type === 'multi-select'
                            ? []
                            : '';
                        handleColumnFilterChange(filter.key, clearValue);
                      }}
                      className="text-primaryColor-dark/60 hover:text-primaryColor-dark transition-colors shrink-0"
                      title={t('common:tableControls.removeFilterTitle', {
                        filter: filter.label,
                        defaultValue: 'Remove {{filter}} filter',
                      })}
                      aria-label={t('common:tableControls.removeFilterTitle', {
                        filter: filter.label,
                        defaultValue: 'Remove {{filter}} filter',
                      })}
                    >
                      <FontAwesomeIcon
                        icon={faIcons.faTimes}
                        className="text-[10px]"
                      />
                    </button>
                  </div>
                }
                popoverContent={
                  <div className="p-2 text-sm max-w-[300px]">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-medium text-primaryColor">{filter.label}:</span>
                      {filter.affectedColumns.length > 1 && (
                        <div className="text-xs text-gray-500 italic">
                          {t('common:tableControls.affectsColumns', {
                            count: filter.affectedColumns.length,
                            defaultValue: '(affects {{count}} column)',
                            defaultValue_other: '(affects {{count}} columns)',
                          })}
                        </div>
                      )}
                    </div>
                    {Array.isArray(filter.value) ? filter.value.join(', ') : filter.value}
                  </div>
                }
                position="top"
                trigger="hover"
                openDelay={0}
                closeDelay={0}
              />
            ))}
            <button
              onClick={handleClearAllColumnFilters}
              className="text-xs text-slate-500 hover:text-slate-700 font-medium underline underline-offset-2 transition-colors px-1"
              title={t('filters.clearAllFilters', 'Clear all filters')}
            >
              {t('actions.clearAll', 'Clear all')}
            </button>
          </>
        )}
      </div>
    );
  })();

  // A filtered-but-empty result must read "no matches" (with a way to clear),
  // not the first-run "get started" copy. Column filters are known here;
  // segment / search-form state the table can't see arrives via `isFiltered`.
  const filtersActive = activeColumnFilters.length > 0 || Boolean(isFiltered);
  const clearAllFilters = onClearFilters || handleClearAllColumnFilters;

  // ── Automatic bulk-selection dock ──
  // Tables that don't wire a bespoke selectionDock get their bulk verbs
  // (actions with bulkAction !== false) as a floating BulkActionDock.
  // Overlay-confirmed verbs (the legacy popover confirms) route through
  // CgConfirmDialog instead — one confirmation grammar everywhere.
  const bulkPayload = () => (selectedItems.length === 1 ? selectedItems[0] : selectedItems);
  // Identify the cancel button by its VARIANT, not by the string "Cancel".
  // Every caller in the tree declares it as `{ label: 'Cancel', variant: 'none' }`,
  // and once labels are translated a label comparison stops matching — which
  // would silently promote Cancel to the confirm dialog's primary action.
  const overlayPrimaryBtn = (overlay) =>
    overlay?.buttons?.find((b) => b.variant === 'danger') ??
    overlay?.buttons?.filter((b) => b.variant !== 'none').slice(-1)[0];
  // The overlay config's own `Cancel` entry is dropped — CgConfirmDialog owns
  // the cancel affordance, and every legacy Cancel handler is a no-op. Reads
  // its label off the config for callers with a custom cancel wording, keyed
  // by VARIANT like `overlayPrimaryBtn` above — a `label === 'Cancel'` string
  // match stops working the moment a caller translates that label (several
  // already do, e.g. CustomBenchmarksTable's `t('common:actions.cancel', …)`).
  const overlayCancelLabel = (overlay) =>
    overlay?.buttons?.find((b) => b.variant === 'none')?.label ||
    t('common:actions.cancel', 'Cancel');
  // A row action names itself with `title` and marks itself destructive with `variant: 'danger'`
  // or a danger class (`text-dangerColor`, or the legacy `c-danger`). The dock reads `label` and
  // `tone`, so a delete verb declared the row way arrived unlabelled and un-toned — a bare grey trash icon, where the tables that wire
  // the dock by hand show a red "Delete". Nine tables were on the first path and six on the
  // second, which is the whole difference the user sees.
  const isDestructiveVerb = (action) =>
    action.variant === 'danger' ||
    /\b(c-danger|text-dangerColor)\b/.test(action.className ?? '') ||
    (action.overlay ? overlayPrimaryBtn(action.overlay)?.variant === 'danger' : false);

  const runVerb = (action) => {
    const primaryBtn = action.overlay ? overlayPrimaryBtn(action.overlay) : null;
    return action.overlay
      ? () => setBulkConfirm({ action, primaryBtn })
      : () => action.onClick(bulkPayload());
  };

  const autoSelectionDock =
    !selectionDock && showSelectionToolbar && !loading && selectedItems.length > 0
      ? (() => {
          const bulkVerbs = (actions || []).filter((a) => a.bulkAction !== false);
          if (bulkVerbs.length === 0) return null;

          // The destructive verb routes to the dock's own `onDelete`, which renders the one
          // canonical red Delete button. Reusing it rather than reconstructing a red button
          // here is what makes these tables identical to the hand-wired ones instead of merely
          // similar. A second destructive verb (rare) stays in the generic list.
          const deleteVerb = bulkVerbs.find(isDestructiveVerb);
          const otherVerbs = bulkVerbs.filter((a) => a !== deleteVerb);

          return (
            <BulkActionDock
              selectedCount={selectedItems.length}
              onClear={() => onSelect?.([])}
              onDelete={deleteVerb ? runVerb(deleteVerb) : undefined}
              actions={otherVerbs.map((action) => {
                const primaryBtn = action.overlay ? overlayPrimaryBtn(action.overlay) : null;
                return {
                  // `title` is how a row action names itself; without this fallback a verb with
                  // no explicit `label` renders as a bare icon with no accessible name.
                  label: action.label ?? primaryBtn?.label ?? action.title,
                  icon: action.icon,
                  tone: isDestructiveVerb(action) ? 'danger' : 'default',
                  onClick: runVerb(action),
                };
              })}
            />
          );
        })()
      : null;
  const resolvedSelectionDock = selectionDock ?? autoSelectionDock;

  return (
    <CgPanel
      animate={animate}
      flat={flat}
    >
      <div className={`flow-root relative ${flat ? '' : 'bg-white md:rounded-lg'}`}>
        {/* Flat mode floats the controls ABOVE the card on transparent
            strips so the card itself starts with the column headers —
            no empty white toolbar band inside the table. Bare mode keeps
            the in-card header (the host card owns that row); the legacy
            non-flat mode keeps its old inline toolbar. */}
        {(title ||
          toolbarLeftPrefix ||
          (!(flat && !bare) && inlineFilterControls) ||
          enhancedFilter ||
          tableButtons?.length > 0) && (
          <div
            className={`flex items-center justify-between gap-3 flex-wrap ${
              bare ? 'px-4 py-2.5 bg-white border-b border-slate-100' : flat ? 'pb-2' : 'pb-3'
            }`}
          >
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              {title && <p className="text-sm font-semibold text-slate-800 m-0">{title}</p>}
              {toolbarLeftPrefix}
              {(!flat || bare) && inlineFilterControls}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {enhancedFilter}
              {tableButtons?.map((btn, idx) => getTableButton(btn, idx))}
            </div>
          </div>
        )}

        {/* Bulk-selection dock rides its own strip directly above the
            card, with the rest of the controls (kept out of the toolbar
            row so it doesn't reflow the segments/Filter cluster). The
            strip is sticky so the verbs follow the viewport down a long
            table, parking just below the fixed app header. */}
        {resolvedSelectionDock && (
          <div
            className={`sticky z-30 ${
              bare ? 'px-4 py-2 bg-white border-b border-slate-100' : 'pb-2'
            }`}
            style={{
              top: 'calc(var(--cg-jobs-banner-h, 0px) + var(--cg-header-h, 56px) + 8px)',
            }}
          >
            {resolvedSelectionDock}
          </div>
        )}

        {/* Shared confirmation for the automatic dock's overlay verbs. */}
        <CgConfirmDialog
          open={!!bulkConfirm}
          onOpenChange={(next) => {
            if (!next) setBulkConfirm(null);
          }}
          title={bulkConfirm?.action.overlay?.title}
          cancelLabel={overlayCancelLabel(bulkConfirm?.action.overlay)}
          confirmLabel={bulkConfirm?.primaryBtn?.label || t('common:actions.confirm', 'Confirm')}
          confirmVariant={bulkConfirm?.primaryBtn?.variant === 'danger' ? 'danger' : 'primary'}
          onConfirm={() => {
            bulkConfirm?.primaryBtn?.onClick?.(bulkPayload());
            setBulkConfirm(null);
          }}
        >
          {bulkConfirm?.action.overlay?.body}
        </CgConfirmDialog>

        {/* …and the same grammar for a single row's overlay verb (the legacy
            TableActionOverlay popover, retired in operator-parity phase 5).
            Focus trap, ESC and keyboard reachability come free with the
            dialog; the popover had none of them. */}
        <CgConfirmDialog
          open={!!rowConfirm}
          onOpenChange={(next) => {
            if (!next) setRowConfirm(null);
          }}
          title={rowConfirm?.action.overlay?.title}
          cancelLabel={overlayCancelLabel(rowConfirm?.action.overlay)}
          confirmLabel={
            (rowConfirmPending && rowConfirm?.primaryBtn?.pendingLabel) ||
            rowConfirm?.primaryBtn?.label ||
            t('common:actions.confirm', 'Confirm')
          }
          confirmVariant={rowConfirm?.primaryBtn?.variant === 'danger' ? 'danger' : 'primary'}
          loading={rowConfirmPending}
          onConfirm={async () => {
            const result = rowConfirm?.primaryBtn?.onClick?.(rowConfirm.item);
            // A thenable onClick (a mutation, via mutateAsync) keeps the
            // dialog open and locked until the request settles — see
            // rowConfirmPending above. Sync onClick closes on the same tick.
            if (!result || typeof result.then !== 'function') {
              setRowConfirm(null);
              return;
            }
            setRowConfirmPending(true);
            try {
              await result;
            } catch {
              // Swallowed deliberately: the caller's mutation owns
              // user-facing error surfacing (its own onError/notify) — this
              // handler only needs to know the request settled so it can
              // stop blocking the dialog.
            } finally {
              setRowConfirmPending(false);
              setRowConfirm(null);
            }
          }}
        >
          {rowConfirm?.action.overlay?.body}
        </CgConfirmDialog>

        {/* Active-filter chips get their own transparent strip in flat
            mode — rendered only when filters are active, so nothing
            reserves empty space. */}
        {flat && !bare && inlineFilterControls && (
          <div className="pb-2">{inlineFilterControls}</div>
        )}

        {!flat && <hr className="w-full border-0 border-t border-slate-200 mb-0.5" />}

        {/* In flat mode the card wraps rows AND pagination, and starts
            directly with the column headers — controls float above. */}
        <div
          className={
            flat && !bare && (data?.length > 0 || loading)
              ? 'bg-white border border-slate-200 rounded-xl overflow-hidden'
              : ''
          }
        >
          <div className="overflow-x-auto relative">
            {/* Top progress bar — shown during background refetches (page change, filter change)
              when data already exists. The skeleton handles the initial empty load. */}
            {loading && data?.length > 0 && (
              <div
                className="absolute top-0 left-0 right-0 h-0.5 bg-slate-100 overflow-hidden z-20 pointer-events-none rounded-t-xl"
                aria-hidden="true"
              >
                <div className="h-full w-1/3 bg-primaryColor animate-progressIndeterminate" />
              </div>
            )}
            <div className="min-w-full">
              {loading && !data?.length && (
                <CgTableSkeleton
                  rows={pagination?.limit || 5}
                  columns={headers?.length || 5}
                  showPagination={!!pagination}
                  selectable={selectable}
                  showActions={!!actions && showActions}
                />
              )}
              {!loading &&
                data?.length === 0 &&
                (filtersActive ? (
                  <CgEmptyState
                    intent="no-results"
                    onClearFilters={clearAllFilters}
                  />
                ) : (
                  <CgNoResults
                    variant={emptyStateVariant}
                    onImport={onEmptyStateImport}
                    onCreate={onEmptyStateCreate}
                    onGenerateReport={onEmptyStateGenerateReport}
                    createLabel={emptyStateCreateLabel}
                    importLabel={emptyStateImportLabel}
                    title={emptyStateTitle}
                    description={emptyStateDescription}
                    onNavigate={onEmptyStateNavigate}
                    navigateLabel={emptyStateNavigateLabel}
                  />
                ))}
              {data?.length > 0 && (
                <div
                  className={`overflow-x-auto ${
                    bare || flat ? '' : 'border border-slate-100 rounded-xl'
                  }`}
                >
                  <table
                    className={`min-w-full divide-y divide-slate-100 ${
                      fixedLayout ? 'table-fixed w-full' : ''
                    }`}
                  >
                    <thead>
                      {useHeaderReorder ? (
                        <DndContext
                          sensors={columnDragSensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleColumnDragEnd}
                        >
                          <tr>
                            {selectable && (
                              <th
                                className={`px-3 py-2 ${thClass}`}
                                style={{ width: '40px' }}
                              >
                                <div className="text-base">
                                  <CgCheckbox
                                    formControl={{
                                      name: 'selectAll',
                                      value: selectAll,
                                      key: 'selectAll',
                                    }}
                                    onChange={(e) => handleSelectAll(!selectAll)}
                                    hideLabel={true}
                                    loading={checkboxLoading}
                                  />
                                </div>
                              </th>
                            )}
                            {lockedTableHeaders.map((header) => (
                              <th
                                key={header.key}
                                className={`${header.type === 'icon' ? 'pl-3 pr-1' : 'px-3'} py-2 ${thClass} ${header.type === 'icon' ? 'w-[48px]' : ''}`}
                              >
                                <TableHeaderCellContent
                                  header={header}
                                  {...headerCellCommonProps}
                                />
                              </th>
                            ))}
                            <SortableContext
                              items={reorderableColumnIds}
                              strategy={horizontalListSortingStrategy}
                            >
                              {reorderableTableHeaders.map((header) => (
                                <SortableTableHeaderCell
                                  key={header.key}
                                  header={header}
                                  {...headerCellCommonProps}
                                />
                              ))}
                            </SortableContext>
                            {actions && showActions && <th className={thClass}></th>}
                          </tr>
                        </DndContext>
                      ) : (
                        <tr>
                          {selectable && (
                            <th
                              className={`px-3 py-2 ${thClass}`}
                              style={{ width: '40px' }}
                            >
                              <div className="text-base">
                                <CgCheckbox
                                  formControl={{
                                    name: 'selectAll',
                                    value: selectAll,
                                    key: 'selectAll',
                                  }}
                                  onChange={(e) => handleSelectAll(!selectAll)}
                                  hideLabel={true}
                                  loading={checkboxLoading}
                                />
                              </div>
                            </th>
                          )}
                          {headers.map((header) => (
                            <th
                              key={header.key}
                              style={header.width ? { width: header.width } : undefined}
                              className={`${header.type === 'icon' ? 'pl-3 pr-1' : 'px-3'} py-2 ${thClass} ${header.type === 'icon' ? 'w-[48px]' : ''}`}
                            >
                              <TableHeaderCellContent
                                header={header}
                                {...headerCellCommonProps}
                              />
                            </th>
                          ))}
                          {actions && showActions && <th className={thClass}></th>}
                        </tr>
                      )}
                    </thead>
                    <tbody
                      className={`bg-white divide-y divide-slate-100 transition-opacity duration-200 ${
                        loading ? 'opacity-60' : ''
                      }`}
                      aria-busy={loading || undefined}
                    >
                      {data?.map((item, index) => {
                        const isSelected = selectedItems.some(
                          (selected) => selected._id === values[index]?._id,
                        );
                        return (
                          <tr
                            className={`group/row hover:bg-slate-50 transition-colors duration-150 ${
                              isSelected ? 'bg-primaryColor/5' : ''
                            } ${getRowClassName ? getRowClassName(item, values[index]) : ''}`}
                            key={`row-${values[index]?._id}`}
                          >
                            {selectable && (
                              <td className="px-3">
                                <CgCheckbox
                                  formControl={{
                                    name: `row-${values[index]?._id}`,
                                    value: isSelected,
                                    key: `row-${values[index]?._id}`,
                                    disabled: !canSelectRow(values[index]),
                                  }}
                                  onChange={(e) => handleRowSelect(values[index], !isSelected)}
                                  hideLabel={true}
                                  loading={checkboxLoading}
                                />
                              </td>
                            )}
                            {headers.map((column) => (
                              <td
                                onClick={() => {
                                  if (column.key === 'actions' || column.suppressPrimaryClick) {
                                    return;
                                  }
                                  onPrimaryClick && onPrimaryClick(item);
                                }}
                                key={column.key}
                                style={column.type === 'icon' ? { width: '48px' } : undefined}
                                className={`group py-3 ${column.type === 'icon' ? 'pl-3 pr-1' : 'px-3'} ${
                                  column.key === 'actions' || column.suppressPrimaryClick
                                    ? ''
                                    : 'cursor-pointer'
                                } ${
                                  // Under a fixed layout the column width is set by
                                  // the header; without this a long unbroken value
                                  // would still overflow its cell. Cells that opt out
                                  // (`allowOverflow`) keep wrapping — hover cards and
                                  // multi-line stacks need it.
                                  fixedLayout && !column.allowOverflow ? 'truncate max-w-0' : ''
                                }`}
                              >
                                {renderCell(item, column)}
                              </td>
                            ))}
                            {actions && showActions && (
                              <td className="px-3">
                                {/* With revealActionsOnHover the actions stay
                                    hidden until the row is hovered or a button
                                    inside takes keyboard focus — the table
                                    reads clean while remaining tabbable. */}
                                <div
                                  className={`flex justify-end items-center gap-1 ${
                                    revealActionsOnHover
                                      ? 'opacity-0 transition-opacity duration-150 group-hover/row:opacity-100 focus-within:opacity-100'
                                      : ''
                                  }`}
                                >
                                  {actions.map((action, idx) => {
                                    const shouldShow = action.show
                                      ? action.show(values[index])
                                      : true;
                                    if (!shouldShow) return null;

                                    // Row verbs and overlay-confirmed row verbs
                                    // render the same button; the overlay ones
                                    // open CgConfirmDialog instead of firing
                                    // straight away (one confirm grammar).
                                    const row = values[index];
                                    // R4 (S7 fix batch 3): `disabled` may be a
                                    // boolean or a per-row predicate — lets a
                                    // caller disable one row's action (e.g. a
                                    // non-idempotent mutation already in flight
                                    // for that row) without hiding it outright.
                                    const isDisabled =
                                      typeof action.disabled === 'function'
                                        ? action.disabled(row)
                                        : !!action.disabled;
                                    return (
                                      <TableActionButton
                                        key={`action-btn-${idx}`}
                                        variant={action.variant}
                                        icon={action.icon}
                                        label={action.label}
                                        title={action.title}
                                        ariaLabel={action.ariaLabel || action.title || action.label}
                                        className={action.className}
                                        style={action.style}
                                        data-testid={action['data-testid']}
                                        disabled={isDisabled}
                                        onClick={() =>
                                          action.overlay
                                            ? setRowConfirm({
                                                action,
                                                primaryBtn: overlayPrimaryBtn(action.overlay),
                                                item: row,
                                              })
                                            : action.onClick(row)
                                        }
                                      />
                                    );
                                  })}
                                </div>
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Only render the footer rule + pagination when there are rows.
            An empty table shows a single empty-state message (no second
            "No results" in the pagination strip); on background refetch with
            data present, pagination stays mounted so the user still sees which
            page they just clicked. */}
          {!flat && data?.length > 0 && (
            <hr className="w-full border-0 border-t border-slate-100 mt-2" />
          )}

          {pagination && data?.length > 0 && (
            <div
              className={`flex flex-wrap items-center justify-between gap-4 pt-3 ${
                bare
                  ? 'px-4 pb-3'
                  : flat
                    ? `px-4 pb-3 ${data?.length > 0 ? 'border-t border-slate-100' : ''}`
                    : 'px-1 pb-1'
              } transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : ''}`}
              aria-busy={loading || undefined}
            >
              <div className="min-w-[140px]">
                {paginationLabel ??
                  (() => {
                    const start = Math.min(
                      (pagination.page - 1) * pagination.limit + 1,
                      pagination.total,
                    );
                    const end = Math.min(pagination.page * pagination.limit, pagination.total);
                    return (
                      <p className="text-sm text-slate-500">
                        {pagination.total > 0
                          ? t('common:pagination.showingRange', {
                              start,
                              end,
                              total: pagination.total,
                              defaultValue: 'Showing {{start}}–{{end}} of {{total}}',
                            })
                          : t('common:emptyState.noResults', 'No results')}
                      </p>
                    );
                  })()}
              </div>
              <Pagination
                {...pagination}
                changePage={(p) => {
                  pagination?.onPageChange(p);
                }}
              />
              <div className="min-w-[140px] flex justify-end">
                <MaxPageResultsSelect
                  options={[5, 10, 25, 50, 100]}
                  value={pagination?.limit}
                  onChange={(value) => {
                    pagination?.onPageChange(1, parseInt(value));
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </CgPanel>
  );
};

export default Table;
